import Sortable from 'sortablejs';
import { DEFAULT_COLUMNS, sortColumnsByOrder, getVisibleColumns, copyColumnConfig } from '../../types/columnTypes';
import { saveUserColumnSettings, getUserInformation, extractColumnSettingsFromUserInfo, extractJobCategoryCode, extractDepartmentCode } from '../userManager/UserManager';
import { generateProjectRoute } from '../../constant/businessTypeConstants';
/**
 * 列管理とドラッグアンドドロップ機能を提供するクラス
 */
export class ColumnManager {
    columns;
    sortable = null;
    panelSortable = null;
    tableElement = null;
    onColumnOrderChanged;
    saveTimeout = null;
    maskedColumnKeys = new Set(); // マスキングされた列のキーを記録
    constructor() {
        this.columns = copyColumnConfig(DEFAULT_COLUMNS);
    }
    /**
     * ユーザー設定を読み込む
     * @param isLoading ローディング表示するかどうか
     */
    async loadUserSettings(isLoading = true) {
        try {
            const userInfo = await getUserInformation(isLoading);
            this.loadUserSettingsFromUserInfo(userInfo);
        }
        catch (error) {
            console.error('ユーザー設定の読み込みに失敗しました:', error);
            this.columns = copyColumnConfig(DEFAULT_COLUMNS);
            console.log('エラー時のデフォルト列数:', this.columns.length);
        }
    }
    /**
     * 既存のユーザー情報からユーザー設定を読み込む
     * @param userInfo ユーザー情報
     */
    loadUserSettingsFromUserInfo(userInfo) {
        console.log('[ColumnManager] loadUserSettingsFromUserInfo called');
        try {
            const columnSettings = extractColumnSettingsFromUserInfo(userInfo);
            if (columnSettings && columnSettings.columns) {
                this.columns = [...columnSettings.columns];
                console.log('ユーザー列設定をget-user-informationから読み込みました');
                console.log('読み込まれた列数:', this.columns.length);
                // DEFAULT_COLUMNSから新しい列を追加
                this.mergeNewColumns();
                // DEFAULT_COLUMNSからマスキング設定をマージ
                this.mergeMaskingSettings();
            }
            else {
                this.columns = copyColumnConfig(DEFAULT_COLUMNS);
                console.log('デフォルト列設定を使用します');
                console.log('デフォルト列数:', this.columns.length);
                console.log('DEFAULT_COLUMNS内容（最初の5列）:', DEFAULT_COLUMNS.slice(0, 5));
            }
            // マスキング設定に基づいて列を非表示化
            this.applyColumnMasking(userInfo);
        }
        catch (error) {
            console.error('ユーザー設定の読み込みに失敗しました:', error);
            this.columns = copyColumnConfig(DEFAULT_COLUMNS);
            console.log('エラー時のデフォルト列数:', this.columns.length);
            // エラー時でもマスキングを適用
            this.applyColumnMasking(userInfo);
        }
    }
    /**
     * DEFAULT_COLUMNSからユーザー設定に存在しない新しい列を追加する
     */
    mergeNewColumns() {
        console.log('[ColumnManager] 新しい列をマージ中...');
        const userColumnKeys = new Set(this.columns.map(col => col.key));
        const newColumns = [];
        DEFAULT_COLUMNS.forEach(defaultColumn => {
            if (!userColumnKeys.has(defaultColumn.key)) {
                // ユーザー設定に存在しない列を追加
                newColumns.push({ ...defaultColumn });
                console.log(`[ColumnManager] 新しい列を追加: ${defaultColumn.label} (key: ${defaultColumn.key})`);
            }
        });
        if (newColumns.length > 0) {
            this.columns = [...this.columns, ...newColumns];
            console.log(`[ColumnManager] ${newColumns.length}個の新しい列を追加しました`);
        }
        else {
            console.log('[ColumnManager] 追加する新しい列はありません');
        }
    }
    /**
     * DEFAULT_COLUMNSからマスキング設定、必須フラグ、列幅をマージする
     */
    mergeMaskingSettings() {
        console.log('[ColumnManager] マスキング設定、必須フラグ、列幅をマージ中...');
        this.columns.forEach(column => {
            const defaultColumn = DEFAULT_COLUMNS.find(dc => dc.key === column.key);
            if (defaultColumn) {
                if (defaultColumn.mask_job_category_code) {
                    column.mask_job_category_code = defaultColumn.mask_job_category_code;
                }
                if (defaultColumn.mask_department_code) {
                    column.mask_department_code = defaultColumn.mask_department_code;
                }
                // 必須フラグもマージ
                if (defaultColumn.required) {
                    column.required = defaultColumn.required;
                }
                // 列幅もマージ（ユーザー設定がない、undefined、null、空文字列の場合にデフォルト値を適用）
                if (defaultColumn.width && (!column.width || column.width === '' || column.width === 'undefined')) {
                    column.width = defaultColumn.width;
                    console.log(`[ColumnManager] 列「${column.label}」にデフォルト幅を適用: ${defaultColumn.width}`);
                }
            }
        });
        console.log('[ColumnManager] マスキング設定、必須フラグ、列幅のマージ完了');
    }
    /**
     * マスキング設定に基づいて列を非表示化する
     * @param userInfo ユーザー情報
     */
    applyColumnMasking(userInfo) {
        console.log('[ColumnManager] applyColumnMasking called');
        console.log('[ColumnManager] userInfo:', userInfo);
        const jobCategoryCode = extractJobCategoryCode(userInfo);
        const departmentCode = extractDepartmentCode(userInfo);
        console.log('[ColumnManager] ユーザー職種コード:', jobCategoryCode);
        console.log('[ColumnManager] ユーザー部署コード:', departmentCode);
        // マスキングされた列のキーをクリア
        this.maskedColumnKeys.clear();
        let maskedCount = 0;
        this.columns.forEach(column => {
            // 職種コードによるマスキングチェック
            if (column.mask_job_category_code && jobCategoryCode) {
                if (column.mask_job_category_code.includes(jobCategoryCode)) {
                    column.visible = false;
                    this.maskedColumnKeys.add(column.key);
                    maskedCount++;
                    console.log(`[ColumnManager] 列「${column.label}」を職種コード ${jobCategoryCode} により非表示化`);
                }
            }
            // 部署コードによるマスキングチェック
            if (column.mask_department_code && departmentCode) {
                if (column.mask_department_code.includes(departmentCode)) {
                    column.visible = false;
                    this.maskedColumnKeys.add(column.key);
                    maskedCount++;
                    console.log(`[ColumnManager] 列「${column.label}」を部署コード ${departmentCode} により非表示化`);
                }
            }
        });
        console.log(`[ColumnManager] マスキング処理完了: ${maskedCount}列を非表示化`);
        console.log(`[ColumnManager] マスキングされた列キー:`, Array.from(this.maskedColumnKeys));
    }
    /**
     * ユーザー設定を保存する（デバウンス処理付き）
     * @param isLoading ローディング表示するかどうか
     */
    async saveUserSettings(isLoading = false) {
        // 既存のタイマーをクリア
        if (this.saveTimeout) {
            clearTimeout(this.saveTimeout);
        }
        // 1秒後に保存実行（デバウンス）
        this.saveTimeout = setTimeout(async () => {
            try {
                await saveUserColumnSettings({
                    columns: this.columns,
                    lastUpdated: new Date().toISOString()
                }, isLoading);
                console.log('列設定をDBに保存しました');
            }
            catch (error) {
                console.error('列設定の保存に失敗しました:', error);
            }
        }, 1000);
    }
    /**
     * ドラッグアンドドロップを初期化する
     */
    initializeDragDrop(tableElement, onColumnOrderChanged) {
        this.tableElement = tableElement;
        this.onColumnOrderChanged = onColumnOrderChanged;
        const headerRow = tableElement.querySelector('thead tr');
        if (!headerRow) {
            console.error('テーブルヘッダーが見つかりません');
            return;
        }
        // 既存のSortableインスタンスを破棄
        if (this.sortable) {
            this.sortable.destroy();
        }
        // SortableJSを初期化
        this.sortable = Sortable.create(headerRow, {
            animation: 150,
            ghostClass: 'sortable-ghost',
            chosenClass: 'sortable-chosen',
            dragClass: 'sortable-drag',
            handle: '.column-drag-handle',
            onStart: (evt) => {
                console.log('ドラッグ開始:', evt.oldIndex);
                this.addDragStyles();
            },
            onEnd: (evt) => {
                console.log('ドラッグ終了:', evt.oldIndex, '->', evt.newIndex);
                this.removeDragStyles();
                if (evt.oldIndex !== evt.newIndex && evt.oldIndex !== undefined && evt.newIndex !== undefined) {
                    this.moveColumn(evt.oldIndex, evt.newIndex);
                }
            }
        });
        // リサイズ機能を初期化
        this.initializeResize();
        console.log('ドラッグアンドドロップとリサイズを初期化しました');
    }
    /**
     * リサイズ機能を初期化する
     */
    initializeResize() {
        if (!this.tableElement)
            return;
        const resizeHandles = this.tableElement.querySelectorAll('.resize-handle');
        resizeHandles.forEach((handle) => {
            const th = handle.closest('th');
            if (!th)
                return;
            let startX = 0;
            let startWidth = 0;
            const onMouseDown = (e) => {
                e.preventDefault();
                e.stopPropagation();
                startX = e.pageX;
                startWidth = th.offsetWidth;
                document.addEventListener('mousemove', onMouseMove);
                document.addEventListener('mouseup', onMouseUp);
            };
            const onMouseMove = (e) => {
                const diff = e.pageX - startX;
                const newWidth = Math.max(50, startWidth + diff); // 最小50px
                this.updateColumnWidth(th, newWidth);
            };
            const onMouseUp = () => {
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);
                // リサイズ後の列幅を保存
                const columnKey = th.getAttribute('data-column-key');
                if (columnKey) {
                    const column = this.columns.find(col => col.key === columnKey);
                    if (column) {
                        column.width = `${th.offsetWidth}px`;
                        this.saveUserSettings();
                        console.log(`列「${column.label}」の幅を${column.width}に更新しました`);
                    }
                }
            };
            handle.addEventListener('mousedown', onMouseDown);
        });
        console.log('リサイズ機能を初期化しました');
    }
    /**
     * 列幅を更新する
     */
    updateColumnWidth(th, width) {
        if (!this.tableElement)
            return;
        const columnIndex = Array.from(th.parentElement.children).indexOf(th);
        // ヘッダーの幅を更新
        th.style.width = `${width}px`;
        th.style.minWidth = `${width}px`;
        // データ行のセルも更新
        const rows = this.tableElement.querySelectorAll('tbody tr');
        rows.forEach(row => {
            const cell = row.children[columnIndex];
            if (cell) {
                cell.style.width = `${width}px`;
                cell.style.minWidth = `${width}px`;
            }
        });
    }
    /**
     * 列を移動する
     */
    moveColumn(oldIndex, newIndex) {
        if (!this.tableElement)
            return;
        // 表示中の列設定を取得
        const visibleColumns = getVisibleColumns(sortColumnsByOrder(this.columns));
        if (oldIndex >= visibleColumns.length || newIndex >= visibleColumns.length) {
            console.error('無効な列インデックス:', oldIndex, newIndex);
            return;
        }
        // 列設定の順序を更新
        const movedColumn = visibleColumns[oldIndex];
        const targetColumn = visibleColumns[newIndex];
        // 全ての列の order を更新
        this.updateColumnOrder(movedColumn, targetColumn, oldIndex < newIndex);
        // テーブルのDOM要素を移動
        this.moveTableColumnDOM(oldIndex, newIndex);
        // コールバック実行（パネル更新を含む）
        if (this.onColumnOrderChanged) {
            this.onColumnOrderChanged(this.columns);
        }
        // 設定を保存
        this.saveUserSettings();
    }
    /**
     * 列設定の順序を更新する（列設定ダイアログ）
     */
    updateColumnOrder(movedColumn, targetColumn, moveRight) {
        const targetOrder = targetColumn.order;
        const movedOrder = movedColumn.order;
        // 移動する列の新しい順序を設定
        movedColumn.order = targetOrder;
        // 他の列の順序を調整
        this.columns.forEach(col => {
            if (col.key === movedColumn.key)
                return;
            if (moveRight) {
                // 右に移動する場合
                if (col.order > movedOrder && col.order <= targetOrder) {
                    col.order--;
                }
            }
            else {
                // 左に移動する場合
                if (col.order >= targetOrder && col.order < movedOrder) {
                    col.order++;
                }
            }
        });
        console.log('列順序を更新しました:', movedColumn.key, movedOrder, '->', targetOrder);
    }
    /**
     * テーブルのDOM要素を移動する
     */
    moveTableColumnDOM(oldIndex, newIndex) {
        if (!this.tableElement)
            return;
        const rows = this.tableElement.querySelectorAll('tr');
        rows.forEach(row => {
            const cells = Array.from(row.children);
            if (oldIndex >= cells.length || newIndex >= cells.length)
                return;
            const movedCell = cells[oldIndex];
            row.removeChild(movedCell);
            if (newIndex >= cells.length) {
                row.appendChild(movedCell);
            }
            else {
                row.insertBefore(movedCell, cells[newIndex]);
            }
        });
    }
    /**
     * ドラッグ中のスタイルを追加
     */
    addDragStyles() {
        if (!this.tableElement)
            return;
        this.tableElement.classList.add('column-dragging');
    }
    /**
     * ドラッグ中のスタイルを削除
     */
    removeDragStyles() {
        if (!this.tableElement)
            return;
        this.tableElement.classList.remove('column-dragging');
    }
    /**
     * テーブルHTMLを生成する
     */
    generateTableHTML(data) {
        const visibleColumns = getVisibleColumns(sortColumnsByOrder(this.columns));
        // ヘッダー行を生成
        const headerCells = visibleColumns.map(col => `<th data-column-key="${col.key}" style="width: ${col.width}; min-width: ${col.width};">
        <div class="column-header">
          <span class="column-drag-handle">⋮⋮</span>
          <span class="column-label">${col.label}</span>
          <span class="resize-handle"></span>
        </div>
      </th>`).join('');
        // データ行を生成
        const dataRows = data.map(project => {
            const cells = visibleColumns.map(col => {
                const value = project[col.key];
                // null/undefinedを空文字列に変換
                const displayValue = value === null || value === undefined ? '' : value;
                if (col.key === 'projectNo' && displayValue) {
                    // 業務種別に基づいてルートを決定
                    const href = generateProjectRoute(project.businessType, String(displayValue));
                    return `<td><a href="${href}" data-link data-project-id="${displayValue}">${displayValue}</a></td>`;
                }
                return `<td>${displayValue}</td>`;
            }).join('');
            return `<tr>${cells}</tr>`;
        }).join('');
        return `
      <thead>
        <tr>${headerCells}</tr>
      </thead>
      <tbody>
        ${dataRows}
      </tbody>
    `;
    }
    /**
     * 列の表示/非表示を切り替える
     */
    toggleColumnVisibility(columnKey, visible) {
        const column = this.columns.find(col => col.key === columnKey);
        if (column) {
            // 必須列は非表示化を防止
            if (column.required && !visible) {
                console.warn('必須列は非表示化できません:', columnKey);
                return;
            }
            column.visible = visible;
            this.saveUserSettings();
            console.log('列の表示状態を変更しました:', columnKey, visible);
        }
    }
    /**
     * デフォルト設定にリセットする
     * @param isLoading ローディング表示するかどうか
     */
    async resetToDefault(isLoading = true) {
        try {
            this.columns = copyColumnConfig(DEFAULT_COLUMNS);
            console.log('デフォルト設定を再読み込みしました。列数:', this.columns.length);
            await saveUserColumnSettings({
                columns: this.columns,
                lastUpdated: new Date().toISOString()
            }, isLoading);
            console.log('列設定をデフォルトにリセットしてDBに保存しました');
        }
        catch (error) {
            console.error('デフォルト設定の保存に失敗しました:', error);
            // フォールバック: 最低限の設定を使用
            this.columns = copyColumnConfig(DEFAULT_COLUMNS);
        }
    }
    /**
     * 表示中の列設定を取得する
     */
    getVisibleColumns() {
        return getVisibleColumns(sortColumnsByOrder(this.columns));
    }
    /**
     * 列設定パネル内でのドラッグアンドドロップを初期化する
     */
    initializePanelDragDrop(panelElement, onOrderChanged) {
        const columnList = panelElement.querySelector('.column-list');
        if (!columnList) {
            console.error('列リスト要素が見つかりません');
            return;
        }
        // 既存のパネル用Sortableインスタンスを破棄
        if (this.panelSortable) {
            this.panelSortable.destroy();
        }
        // パネル用SortableJSを初期化
        this.panelSortable = Sortable.create(columnList, {
            animation: 150,
            ghostClass: 'panel-sortable-ghost',
            chosenClass: 'panel-sortable-chosen',
            dragClass: 'panel-sortable-drag',
            handle: '.column-drag-handle-panel',
            onStart: (evt) => {
                console.log('パネル内ドラッグ開始:', evt.oldIndex);
            },
            onEnd: (evt) => {
                console.log('パネル内ドラッグ終了:', evt.oldIndex, '->', evt.newIndex);
                if (evt.oldIndex !== evt.newIndex && evt.oldIndex !== undefined && evt.newIndex !== undefined) {
                    this.movePanelColumn(evt.oldIndex, evt.newIndex);
                    if (onOrderChanged) {
                        onOrderChanged();
                    }
                }
            }
        });
        console.log('パネル内ドラッグアンドドロップを初期化しました');
    }
    /**
     * パネル内での列順序を移動する
     */
    movePanelColumn(oldIndex, newIndex) {
        // 現在の順序でソートされた列配列を取得（マスキングされた列を除外）
        const sortedColumns = sortColumnsByOrder(this.columns).filter(col => !this.maskedColumnKeys.has(col.key));
        if (oldIndex >= sortedColumns.length || newIndex >= sortedColumns.length) {
            console.error('無効なパネル列インデックス:', oldIndex, newIndex);
            return;
        }
        // 移動する列と対象列を取得
        const movedColumn = sortedColumns[oldIndex];
        const targetColumn = sortedColumns[newIndex];
        // 列順序を更新
        this.updateColumnOrder(movedColumn, targetColumn, oldIndex < newIndex);
        // 設定を保存
        this.saveUserSettings();
        console.log('パネル内列順序を更新しました:', movedColumn.key, oldIndex, '->', newIndex);
    }
    /**
     * 列設定パネルのHTMLを生成する
     */
    generateColumnSettingsPanelHTML() {
        const sortedColumns = sortColumnsByOrder(this.columns);
        // マスキングされた列を除外
        const visibleColumnsForPanel = sortedColumns.filter(col => !this.maskedColumnKeys.has(col.key));
        console.log(`[ColumnManager] 列設定パネル: ${visibleColumnsForPanel.length}列を表示 (${this.maskedColumnKeys.size}列をマスキング)`);
        const columnItems = visibleColumnsForPanel.map((col, index) => {
            const checkedAttr = col.visible ? 'checked' : '';
            const displayOrder = index + 1; // マスキング除外後の実際の表示順
            const isRequired = col.required === true;
            console.log(`列 ${col.key} (${col.label}): visible=${col.visible}, checked=${checkedAttr}, displayOrder=${displayOrder}, required=${isRequired}`);
            // 必須列の場合はチェックボックスを非表示化（常に表示）
            const checkboxHTML = isRequired
                ? '' // 必須列はチェックボックスなし
                : `<input type="checkbox" class="column-checkbox" data-column-key="${col.key}" ${checkedAttr}>`;
            return `
        <div class="column-item ${isRequired ? 'required-column' : ''}" data-column-key="${col.key}">
          <span class="column-drag-handle-panel" title="ドラッグして順序を変更">⋮⋮</span>
          ${checkboxHTML}
          <span class="column-name">${col.label}</span>
          <span class="column-order">${displayOrder}</span>
        </div>
      `;
        }).join('');
        return `
      <div class="panel-header">
        <h3>列設定</h3>
        <button class="reset-button">リセット</button>
        <button class="close-button">×</button>
      </div>
      <div class="column-list">
        ${columnItems}
      </div>
      <div class="panel-actions">
        <span class="save-indicator">保存済み</span>
      </div>
    `;
    }
    /**
     * SortableJSインスタンスを破棄する
     */
    destroy() {
        if (this.sortable) {
            this.sortable.destroy();
            this.sortable = null;
        }
        if (this.panelSortable) {
            this.panelSortable.destroy();
            this.panelSortable = null;
        }
        if (this.saveTimeout) {
            clearTimeout(this.saveTimeout);
            this.saveTimeout = null;
        }
    }
}
